import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { peekVisitorId } from "@/lib/analytics";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [businessFeaturesEnabled, setBusinessFeaturesEnabled] = useState(false);
  const [platformLabelsEnabled, setPlatformLabelsEnabled] = useState(false);
  const [buyerPassEnabled, setBuyerPassEnabled] = useState(false);
  const [firearmsListingEnabled, setFirearmsListingEnabled] = useState(false);
  // ADMIN MODE is a per-session CHOICE, never derived from role. Default USER MODE on every
  // fresh login (cleared in applyAuth/logout). Survives reloads within the same tab (sessionStorage)
  // so an admin who entered the portal isn't kicked back to USER MODE on refresh.
  const [adminMode, setAdminModeState] = useState(() => {
    try { return sessionStorage.getItem("os_admin_mode") || "user"; } catch { return "user"; }
  });
  const setAdminMode = useCallback((mode) => {
    const m = mode || "user";
    setAdminModeState(m);
    try {
      if (m !== "user") sessionStorage.setItem("os_admin_mode", m);
      else sessionStorage.removeItem("os_admin_mode");
    } catch {}
  }, []);

  // Public feature flags — fail CLOSED on the client (default false) so gated surfaces stay hidden
  // if the flag fetch fails. Switches: BUSINESS_FEATURES_ENABLED, PLATFORM_LABELS_ENABLED (buy-a-label).
  useEffect(() => {
    api.get("/config/flags")
      .then(({ data }) => {
        setBusinessFeaturesEnabled(!!data?.business_features_enabled);
        setPlatformLabelsEnabled(!!data?.platform_labels_enabled);
        setBuyerPassEnabled(!!data?.buyer_pass_enabled);
        setFirearmsListingEnabled(!!data?.firearms_listing_enabled);
      })
      .catch(() => { setBusinessFeaturesEnabled(false); setPlatformLabelsEnabled(false); setBuyerPassEnabled(false); setFirearmsListingEnabled(false); });
  }, []);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("os_token");
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    // Only a genuine auth rejection (HTTP 401 — bad/expired token or a token_version bump) means the
    // session is dead → clear the token and show logged-out. A TRANSIENT failure (network / 5xx /
    // timeout, e.g. the backend restarting during a deploy) must NEVER discard a valid token — that
    // was logging everyone out on redeploy. Retry with backoff; if it still won't resolve, KEEP the
    // token so a later refresh/navigation restores the session automatically (no re-login).
    for (let attempt = 0; attempt < 4; attempt++) {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        setLoading(false);
        return;
      } catch (err) {
        if (err?.response?.status === 401) {
          localStorage.removeItem("os_token");
          setUser(false);
          setLoading(false);
          return;
        }
        if (attempt < 3) await new Promise((r) => setTimeout(r, 500 * 2 ** attempt)); // 0.5s, 1s, 2s
      }
    }
    // Still unreachable after retries: do NOT delete the token. Render logged-out for now; the
    // preserved token lets the next refresh recover the session once the API is back.
    setUser((prev) => (prev && typeof prev === "object" ? prev : false));
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const applyAuth = (token, userData) => {
    localStorage.setItem("os_token", token);
    setUser(userData);
    setLoading(false);
    setAdminMode("user"); // every fresh login starts in USER MODE — role is not mode
    // Identity stitching: bind this browser's prior anonymous visitor_id to the account
    // so the analytics dashboard can de-fragment the unique-visitor count.
    const vid = peekVisitorId();
    if (vid) api.post("/analytics/stitch", { visitor_id: vid }).catch(() => {});
  };

  const logout = useCallback(async () => {
    // Kill the session SERVER-SIDE first — POST /auth/logout bumps token_version so the presented
    // JWT stops validating on the very next request across every worker. The token must still be
    // in localStorage for this call, so we do it BEFORE clearing. Short timeout: a network hiccup
    // must NEVER trap a user in a signed-in state. On ANY failure (error/timeout) we log it and
    // still clear the local session — a stuck sign-out is worse than a missed server bump.
    try {
      await api.post("/auth/logout", null, { timeout: 5000 });
    } catch (e) {
      console.warn("[auth] server-side logout failed; clearing local session anyway", e);
    } finally {
      localStorage.removeItem("os_token");
      localStorage.removeItem("os_last_activity");
      setUser(false);
      setAdminMode("user");
    }
  }, [setAdminMode]);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, refresh, applyAuth, logout, businessFeaturesEnabled, platformLabelsEnabled, buyerPassEnabled, firearmsListingEnabled, adminMode, setAdminMode }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
